import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { GoogleLogin } from "@react-oauth/google";
import { showSuccess, showError } from "../components/dialogHelper";
import DemoModal from "../components/DemoModal";
import DashboardAd from "../components/DashboardAd";
import logo from "../images/muyaz_small.png";
import {
  FaCodeBranch,
  FaHeadset,
  FaArrowRight,
  FaBars,
  FaTimes,
  FaCheck,
  FaCheckCircle,
  FaChartLine,
  FaBoxes,
  FaFileInvoice,
  FaShoppingCart,
  FaUsers,
  FaBrain,
  FaShieldAlt,
  FaBuilding,
  FaEnvelope,
  FaLock,
  FaKey,
  FaPlay,
  FaWhatsapp,
  FaBolt,
  FaMobileAlt,
  FaGlobeAfrica,
  FaUniversity,
  FaQuoteLeft,
  FaArrowDown,
  FaRocket,
} from "react-icons/fa";

import apiRequest from "../services/api";

/* =========================================================
   DATA
========================================================= */

const BENEFITS = [
  {
    icon: <FaChartLine />,
    title: "Know your numbers",
    desc: "See sales, expenses, payments, and business performance without waiting for manual reports.",
  },
  {
    icon: <FaBoxes />,
    title: "Control your stock",
    desc: "Track inventory in real time, manage branches, batches, and stock movement.",
  },
  {
    icon: <FaFileInvoice />,
    title: "Simplify accounting",
    desc: "Keep your financial records organized with integrated double-entry accounting.",
  },
  {
    icon: <FaUsers />,
    title: "Manage your business",
    desc: "Bring customers, employees, suppliers, and operations into one connected system.",
  },
];

const MODULES = [
  {
    icon: <FaShoppingCart />,
    title: "Sales & POS",
    desc: "Fast checkout, invoicing, credit sales, receipts, and customer management.",
  },
  {
    icon: <FaBoxes />,
    title: "Inventory",
    desc: "Real-time stock, multi-branch tracking, expiry, and stock movement.",
  },
  {
    icon: <FaBuilding />,
    title: "Purchases",
    desc: "Supplier management, purchasing, receiving, and cost tracking.",
  },
  {
    icon: <FaFileInvoice />,
    title: "Accounting",
    desc: "Journals, balance sheet, profit and loss, and financial reporting.",
  },
  {
    icon: <FaUsers />,
    title: "HR & Payroll",
    desc: "Employees, payroll, advances, attendance, and workforce records.",
  },
  {
    icon: <FaBrain />,
    title: "AI Insights",
    desc: "Identify trends, stock risks, and business opportunities faster.",
    highlight: true,
  },
];

const LOCAL_FEATURES = [
  {
    icon: <FaGlobeAfrica />,
    title: "Built for Ethiopian businesses",
    desc: "Designed around the realities of local SMEs.",
  },
  {
    icon: <FaFileInvoice />,
    title: "Tax ready",
    desc: "VAT, withholding, payroll tax, and other business workflows.",
  },
  {
    icon: <FaUniversity />,
    title: "Payments & banking",
    desc: "Designed to support modern payment and banking integrations.",
  },
  {
    icon: <FaMobileAlt />,
    title: "Access anywhere",
    desc: "Manage your business wherever you are.",
  },
];

const PLANS = [
  {
    name: "Starter",
    description: "For small businesses getting organized.",
    price: "3,990",
    period: "/ month",
    features: [
      "Core ERP modules",
      "Sales & inventory",
      "Accounting",
      "Business reports",
      "Standard support",
    ],
  },
  {
    name: "Business",
    description: "For growing businesses that need more control.",
    price: "39,900",
    period: "/ year",
    badge: "BEST VALUE",
    highlight: true,
    features: [
      "Everything in Starter",
      "Priority support",
      "Higher AI usage",
      "Growing team access",
    ],
  },
  {
    name: "Enterprise",
    description: "For larger or more complex operations.",
    price: "Custom",
    period: "",
    features: [
      "Everything in Business",
      "Advanced configuration",
      "Dedicated support",
      "Custom integrations",
      "Custom development as per your needs",
    ],
  },
];

/* =========================================================
   COMPONENT
========================================================= */

const HomePage = ({ onLogin }) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  /* -------------------------
     EXISTING AUTH STATE
  ------------------------- */

  const [authOpen, setAuthOpen] = useState(false);
  const [view, setView] = useState("login");

  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const [authError, setAuthError] = useState("");
  const [authSuccess, setAuthSuccess] = useState("");
  const [packagesError, setPackagesError] = useState("");
  const [loading, setLoading] = useState(false);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [token, setToken] = useState("");
  const [newPassword, setNewPassword] = useState("");

  const [packages, setPackages] = useState([]);
  const getPackageFeatures = (pkg) => {
    const code = String(pkg.code || "").toUpperCase();

    if (code === "STARTER") {
      return [
        "Core ERP modules",
        "Sales & inventory",
        "Accounting",
        "Business reports",
        "Standard support",
      ];
    }

    if (code === "BUSINESS") {
      return [
        "Everything in Starter",
        "More team members",
        "Higher AI allowance",
        "Priority support",
        "Growing business tools",
      ];
    }

    if (code === "GROWTH") {
      return [
        "Everything in Business",
        "Larger team capacity",
        "Advanced business tools",
        "Higher AI allowance",
        "Priority support",
      ];
    }

    return [
      "Everything in Growth",
      "Custom business configuration",
      "Dedicated support",
      "Custom integrations",
      "Advanced enterprise requirements",
      "Custom development",
    ];
  };

  const getPackageStyle = (pkg) => {
    const code = String(pkg.code || "").toUpperCase();

    if (code === "BUSINESS") {
      return "border-emerald-500/40 bg-emerald-500/5 shadow-2xl shadow-emerald-500/5";
    }

    if (code === "GROWTH") {
      return "border-sky-500/30 bg-slate-900/70";
    }

    if (code === "ENTERPRISE") {
      return "border-violet-500/30 bg-violet-500/5";
    }

    return "border-white/5 bg-slate-900/60";
  };

  const getButtonStyle = (pkg) => {
    const code = String(pkg.code || "").toUpperCase();

    if (code === "BUSINESS") {
      return "bg-emerald-600 hover:bg-emerald-500 text-white";
    }

    if (code === "GROWTH") {
      return "bg-sky-600 hover:bg-sky-500 text-white";
    }

    if (code === "ENTERPRISE") {
      return "bg-violet-600 hover:bg-violet-500 text-white";
    }

    return "border border-white/10 bg-white/5 hover:bg-white/10 text-white";
  };

  const load = useCallback(async () => {
    setIsLoading(true);
    setPackagesError("");

    try {
      const response = await apiRequest("subscriptions/packages");
      const list = Array.isArray(response?.data) ? response.data : [];
      setPackages(list);
    } catch (err) {
      setPackagesError(err?.message || "Failed to load subscription data.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  /* -------------------------
     LANDING PAGE STATE
  ------------------------- */

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [demoOpen, setDemoOpen] = useState(false);

  const [demoForm, setDemoForm] = useState({
    fullName: "",
    businessName: "",
    phone: "",
    type: "",
    email: "",
    message: "",
  });

  const [demoLoading, setDemoLoading] = useState(false);
  const [demoSuccess, setDemoSuccess] = useState("");
  const [demoError, setDemoError] = useState("");
  const WHATSAPP_NUMBER = "251913364827";

  const scrollToSection = (id) => {
    setMobileMenuOpen(false);

    const element = document.getElementById(id);

    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  /* =========================================================
     AUTH LOGIC — PRESERVED
  ========================================================= */

  const resetFeedback = () => {
    setAuthError("");
    setAuthSuccess("");
  };

  const openAuth = (mode = "login") => {
    setView(mode);
    resetFeedback();
    setAuthOpen(true);
  };

  const closeAuth = () => {
    setAuthOpen(false);
    setView("login");
    resetFeedback();
  };

  const finishAuth = (res) => {
    const userToken = res.data?.token ?? res?.token;

    if (userToken) {
      localStorage.setItem("token", userToken);
    }

    localStorage.setItem("user", JSON.stringify(res));

    onLogin?.(res);

    closeAuth();

    navigate("/dashboard");
  };

  const handleCredentialLogin = async (e) => {
    e.preventDefault();

    if (!email || !password) {
      return setAuthError("Please enter your email and password.");
    }

    setLoading(true);
    resetFeedback();

    try {
      const res = await apiRequest("auth/login", {
        method: "POST",
        body: { email, password },
      });
      finishAuth(res);
    } catch (err) {
      if (
        err?.code === "ACCOUNT_LOCKED" ||
        err?.data?.data?.code === "ACCOUNT_LOCKED"
      ) {
        const seconds =
          err?.data?.data?.remainingSeconds ||
          err?.data?.remainingSeconds ||
          60;

        const mins = Math.ceil(seconds / 60);

        setAuthError(
          mins <= 1
            ? "Account temporarily locked. Try again in less than a minute."
            : `Account temporarily locked. Try again in ${mins} minutes.`
        );
      } else {
        setAuthError(err?.message || "Invalid email or password.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();

    if (!email) {
      return setAuthError("Email is required to request a reset token.");
    }

    setLoading(true);
    resetFeedback();

    try {
      await apiRequest("users/forgotPassword", {
        method: "POST",
        body: { email },
      });

      setAuthSuccess("Verification OTP sent to your email.");
      setView("reset");
    } catch (err) {
      setAuthError(
        err?.response?.message ||
          err?.message ||
          "Could not send OTP to your email."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();

    if (!email || !token || !newPassword) {
      return setAuthError("All fields are mandatory.");
    }

    setLoading(true);
    resetFeedback();

    try {
      await apiRequest("users/resetPassword", {
        method: "POST",
        body: {
          email,
          token,
          newPassword,
        },
      });

      setAuthSuccess(
        "Password reset successful. Login with your new password."
      );

      setPassword("");
      setView("login");
    } catch (err) {
      setAuthError(
        err?.response?.message || err?.message || "OTP invalid or expired."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (cred) => {
    try {
      setAuthError("");
      setLoading(true);

      const res = await apiRequest("tenants/googleonboarding", {
        method: "POST",
        body: {
          token: cred.credential,
          type: "id_token",
        },
      });

      finishAuth(res);
    } catch (err) {
      setAuthError(
        err?.response?.message ||
          err?.message ||
          "Unable to complete Google Sign-on."
      );
    } finally {
      setLoading(false);
    }
  };

  /* =========================================================
     DEMO HANDLER
     Replace this later with your own API endpoint.
  ========================================================= */

  const handleDemoChange = (e) => {
    const { name, value } = e.target;

    setDemoForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDemoSubmit = async (e) => {
    e.preventDefault();

    setDemoLoading(true);
    setDemoError("");
    setDemoSuccess("");

    try {
      const response = await apiRequest("subscriptions/leads", {
        method: "POST",
        body: demoForm,
      });
      if (response.success) {
        showSuccess(
          response.message ||
            "Thank you! Your request has been received. We will contact you soon."
        );
      }

      // Clear form
      setDemoForm({
        fullName: "",
        businessName: "",
        phone: "",
        type: "",
        email: "",
        message: "",
      });

      // Close modal after success
      setTimeout(() => {
        setDemoOpen(false);
        setDemoSuccess("");
      }, 2500);
    } catch (err) {
      console.error("Lead submission error:", err);

      setDemoError(
        err?.response?.message ||
          err?.message ||
          "Unable to submit your request. Please try again."
      );
    } finally {
      setDemoLoading(false);
    }
  };

  const handleWhatsAppSubmit = async (e) => {
    e.preventDefault();

    if (!demoForm.fullName?.trim() || !demoForm.phone?.trim()) {
      return setDemoError("Please fill in the required fields.");
    }

    setDemoLoading(true);
    setDemoError("");

    try {
      // Build a clean message for WhatsApp
      const message = [
        "🔔 *New Demo Request – MUYAZ BUSINESS OS*",
        "",
        `*Name:* ${demoForm.fullName.trim()}`,
        `*Business:* ${demoForm.businessName.trim()}`,
        `*Phone:* ${demoForm.phone.trim()}`,
        `*Email:* ${demoForm.email}`,
        `*Type:* ${demoForm.type}`,
        `*Message:* ${demoForm.message}`,
        "",
        `_Submitted from muyaz Business OS landing page_`,
      ]
        .filter(Boolean)
        .join("\n");

      const encoded = encodeURIComponent(message);
      const waUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encoded}`;

      // Open WhatsApp (mobile app or web.whatsapp.com)
      window.open(waUrl, "_blank", "noopener,noreferrer");

      // Optional: also send to your backend later
      // await apiRequest("leads/demo", { method: "POST", body: demoForm });

      setDemoSuccess(true);
    } catch (err) {
      setDemoError(
        "Something went wrong. Please try again or message us on WhatsApp."
      );
    } finally {
      setDemoLoading(false);
    }
  };

  /* =========================================================
     RENDER
  ========================================================= */

  return (
    <div className="min-h-screen overflow-x-hidden bg-slate-950 text-white">
      {/* =====================================================
          NAVBAR
      ===================================================== */}
      <header className="sticky top-0 z-50 border-b border-white/5 bg-slate-950/80 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          {/* BRAND */}

          <button
            type="button"
            onClick={() => scrollToSection("home")}
            className="flex items-center gap-3"
          >
            <div className="flex h-10 w-10 items-center justify-center">
              <img
                src={logo}
                alt="Muyaz"
                className="h-full w-full object-contain"
              />
            </div>

            <div className="text-left">
              <div className="font-bold tracking-tight">MBOS</div>

              <div className="text-[9px] font-semibold uppercase tracking-[0.2em] text-emerald-400">
                Muyaz Business Operating System
              </div>
            </div>
          </button>

          {/* DESKTOP NAV */}

          <nav className="hidden items-center gap-7 md:flex">
            <button
              onClick={() => scrollToSection("features")}
              className="text-sm text-slate-400 transition hover:text-white"
            >
              Features
            </button>

            <button
              onClick={() => scrollToSection("solutions")}
              className="text-sm text-slate-400 transition hover:text-white"
            >
              Solutions
            </button>

            <button
              onClick={() => scrollToSection("why-muyaz")}
              className="text-sm text-slate-400 transition hover:text-white"
            >
              Why MBOS
            </button>

            <button
              onClick={() => scrollToSection("pricing")}
              className="text-sm text-slate-400 transition hover:text-white"
            >
              Pricing
            </button>
          </nav>

          {/* DESKTOP CTA */}

          <div className="hidden items-center gap-3 md:flex">
            <button
              type="button"
              onClick={() => openAuth("login")}
              className="px-3 py-2 text-sm font-semibold text-slate-300 transition hover:text-white"
            >
              Log in
            </button>

            <button
              type="button"
              onClick={() => setDemoOpen(true)}
              className="rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-bold text-white shadow-lg shadow-emerald-600/20 transition hover:bg-emerald-500"
            >
              Book a Demo
            </button>
          </div>

          {/* MOBILE MENU */}

          <button
            type="button"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="rounded-xl border border-white/10 bg-white/5 p-2.5 md:hidden"
          >
            {mobileMenuOpen ? <FaTimes /> : <FaBars />}
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="border-t border-white/5 bg-slate-950 px-4 py-5 md:hidden">
            <div className="flex flex-col gap-4">
              {[
                ["Features", "features"],
                ["Solutions", "solutions"],
                ["Why Muyaz", "why-muyaz"],
                ["Pricing", "pricing"],
              ].map(([label, id]) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => scrollToSection(id)}
                  className="text-left text-sm text-slate-300"
                >
                  {label}
                </button>
              ))}

              <div className="mt-2 grid grid-cols-2 gap-3">
                <button
                  onClick={() => openAuth("login")}
                  className="rounded-xl border border-white/10 px-4 py-3 text-sm font-bold"
                >
                  Log in
                </button>

                <button
                  onClick={() => {
                    setMobileMenuOpen(false);
                    setDemoOpen(true);
                  }}
                  className="rounded-xl bg-emerald-600 px-4 py-3 text-sm font-bold"
                >
                  Book Demo
                </button>
              </div>
            </div>
          </div>
        )}
      </header>
      {/* =====================================================
          HERO
      ===================================================== */}
      <section
        id="home"
        className="relative overflow-hidden border-b border-white/5"
      >
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute left-1/2 top-0 h-[600px] w-[800px] -translate-x-1/2 rounded-full bg-emerald-600/20 blur-[150px]" />
        </div>

        <div className="relative mx-auto max-w-7xl px-4 pb-24 pt-20 sm:px-6 sm:pb-28 sm:pt-28 lg:px-8">
          <div className="mx-auto max-w-4xl text-center">
            <div className="mb-7 inline-flex items-center gap-2 rounded-full border border-emerald-400/20 bg-emerald-500/10 px-4 py-2 text-xs font-bold text-emerald-400">
              <FaBolt />
              BUILT FOR GROWING ETHIOPIAN BUSINESSES
            </div>

            <h1 className="text-4xl font-black leading-[1.05] tracking-tight sm:text-6xl lg:text-7xl">
              Know your business.
              <br />
              <span className="bg-gradient-to-r from-emerald-400 to-teal-300 bg-clip-text text-transparent">
                Grow with confidence.
              </span>
            </h1>

            <p className="mx-auto mt-7 max-w-2xl text-base leading-relaxed text-slate-400 sm:text-lg">
              Manage your sales, stock, expenses, payments, government taxes and
              accounting from one powerful platform built for growing Ethiopian
              businesses.
            </p>

            <div className="mt-9 flex flex-col justify-center gap-3 sm:flex-row">
              <button
                type="button"
                onClick={() => setDemoOpen(true)}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-600 px-7 py-3.5 text-sm font-bold shadow-xl shadow-emerald-600/25 transition hover:bg-emerald-500"
              >
                Book a Free Demo
                <FaArrowRight />
              </button>

              <button
                type="button"
                onClick={() => openAuth("login")}
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/5 px-7 py-3.5 text-sm font-bold text-slate-200 transition hover:bg-white/10"
              >
                Start Free Trial
                <FaRocket />
              </button>
            </div>

            <div className="mt-7 flex flex-wrap justify-center gap-x-6 gap-y-3 text-xs text-slate-500">
              <span className="flex items-center gap-2">
                <FaCheck className="text-emerald-400" />
                No complex setup
              </span>

              <span className="flex items-center gap-2">
                <FaCheck className="text-emerald-400" />
                Secure access
              </span>

              <span className="flex items-center gap-2">
                <FaCheck className="text-emerald-400" />
                Built for Ethiopian SMEs
              </span>
            </div>
          </div>

          {/* PRODUCT PREVIEW */}

          <div className="relative mx-auto mt-16 max-w-6xl">
            <div className="absolute -inset-6 rounded-[3rem] bg-emerald-500/10 blur-3xl" />

            <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-slate-900 shadow-2xl">
              {/* DASHBOARD TOP */}

              <div className="flex items-center justify-between border-b border-white/5 bg-slate-900 px-5 py-4">
                <div className="flex items-center gap-2">
                  <div className="h-3 w-3 rounded-full bg-red-400/50" />
                  <div className="h-3 w-3 rounded-full bg-amber-400/50" />
                  <div className="h-3 w-3 rounded-full bg-emerald-400/50" />
                </div>

                <div className="text-[10px] font-mono text-slate-600">
                  app.muyaz.com
                </div>
              </div>

              {/* MOCK DASHBOARD */}

              <div className="grid min-h-[420px] grid-cols-1 gap-4 bg-slate-950 p-5 md:grid-cols-4">
                <aside className="hidden rounded-2xl border border-white/5 bg-slate-900/60 p-4 md:block">
                  <div className="space-y-4">
                    {[
                      "Dashboard",
                      "Sales",
                      "Inventory",
                      "Purchases",
                      "Accounting",
                      "Reports",
                    ].map((item, index) => (
                      <div
                        key={item}
                        className={`rounded-lg px-3 py-2 text-xs ${
                          index === 0
                            ? "bg-emerald-500/10 font-bold text-emerald-400"
                            : "text-slate-500"
                        }`}
                      >
                        {item}
                      </div>
                    ))}
                  </div>
                </aside>

                <div className="space-y-4 md:col-span-3">
                  <div>
                    <p className="text-xs text-slate-500">Business overview</p>
                  </div>
                  <DashboardAd />
                </div>
              </div>
            </div>
          </div>

          <div className="mt-10 flex justify-center">
            <button
              type="button"
              onClick={() => scrollToSection("features")}
              className="flex flex-col items-center gap-2 text-xs text-slate-500 transition hover:text-emerald-400"
            >
              Explore MBOS
              <FaArrowDown className="animate-bounce" />
            </button>
          </div>
        </div>
      </section>
      {/* =====================================================
          PROBLEM / BENEFITS
      ===================================================== */}
      <section
        id="features"
        className="border-b border-white/5 bg-slate-900/30 py-24"
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-emerald-400">
              One source of truth
            </p>

            <h2 className="mt-4 text-3xl font-black tracking-tight sm:text-5xl">
              Stop guessing,
              <br />
              Start knowing!
            </h2>

            <p className="mt-5 text-slate-400">
              Replace disconnected tools, spreadsheets and manual processes with
              one connected business platform.
            </p>
          </div>

          <div className="mt-16 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {BENEFITS.map((item) => (
              <div
                key={item.title}
                className="rounded-3xl border border-white/5 bg-slate-950 p-6 transition duration-300 hover:-translate-y-1 hover:border-emerald-500/30"
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-500/10 text-xl text-emerald-400">
                  {item.icon}
                </div>

                <h3 className="mt-6 text-lg font-bold">{item.title}</h3>

                <p className="mt-3 text-sm leading-relaxed text-slate-400">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* =====================================================
          SOLUTIONS
      ===================================================== */}
      <section id="solutions" className="py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-16 lg:grid-cols-2">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-emerald-400">
                Everything connected
              </p>

              <h2 className="mt-4 text-3xl font-black leading-tight sm:text-5xl">
                Your entire business,
                <br />
                <span className="text-emerald-400">One platform.</span>
              </h2>

              <p className="mt-6 max-w-xl leading-relaxed text-slate-400">
                MBOS connects the important parts of your business so your
                sales, stock, payments and accounting work together instead of
                living in separate systems.
              </p>

              <button
                type="button"
                onClick={() => setDemoOpen(true)}
                className="mt-8 inline-flex items-center gap-2 font-bold text-emerald-400 transition hover:text-emerald-300"
              >
                See how MBOS works
                <FaArrowRight />
              </button>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              {MODULES.map((module) => (
                <div
                  key={module.title}
                  className={`rounded-2xl border p-6 ${
                    module.highlight
                      ? "border-emerald-500/30 bg-emerald-500/5"
                      : "border-white/5 bg-slate-900/50"
                  }`}
                >
                  <div
                    className={`text-xl ${
                      module.highlight ? "text-emerald-400" : "text-slate-300"
                    }`}
                  >
                    {module.icon}
                  </div>

                  <h3 className="mt-5 font-bold">{module.title}</h3>

                  <p className="mt-2 text-sm leading-relaxed text-slate-400">
                    {module.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      {/* =====================================================
          WHY MUYAZ / LOCAL DIFFERENTIATION
      ===================================================== */}
      <section
        id="why-muyaz"
        className="border-y border-white/5 bg-emerald-950/10 py-24"
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-emerald-400">
              Why MBOS
            </p>

            <h2 className="mt-4 text-3xl font-black sm:text-5xl">
              Built for how your business actually works.
            </h2>

            <p className="mt-5 text-slate-400">
              Global software is often built for global assumptions. MBOS is
              designed around the needs of growing businesses here in Ethiopia.
            </p>
          </div>

          <div className="mt-16 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {LOCAL_FEATURES.map((item) => (
              <div
                key={item.title}
                className="rounded-3xl border border-emerald-500/10 bg-slate-950/70 p-6"
              >
                <div className="text-xl text-emerald-400">{item.icon}</div>

                <h3 className="mt-5 font-bold">{item.title}</h3>

                <p className="mt-3 text-sm leading-relaxed text-slate-400">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* =====================================================
          HOW IT WORKS
      ===================================================== */}
      <section className="py-24">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-emerald-400">
              Getting started
            </p>

            <h2 className="mt-4 text-3xl font-black sm:text-5xl">
              Get started in three simple steps.
            </h2>
          </div>

          <div className="mt-16 grid gap-8 md:grid-cols-3">
            {[
              {
                number: "01",
                title: "Create your account",
                desc: "Start with your gmail /Google account.",
              },
              {
                number: "02",
                title: "Set up your business",
                desc: "Add your business information and configure your workspace.",
              },
              {
                number: "03",
                title: "Start managing",
                desc: "Track your sales, stock, expenses and financial activity.",
              },
            ].map((step) => (
              <div key={step.number} className="relative">
                <div className="text-6xl font-black text-emerald-500/10">
                  {step.number}
                </div>

                <h3 className="mt-4 text-xl font-bold">{step.title}</h3>

                <p className="mt-3 leading-relaxed text-slate-400">
                  {step.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* =====================================================
          TRUST
      ===================================================== */}
      <section className="border-y border-white/5 bg-slate-900/40 py-20">
        <div className="mx-auto max-w-5xl px-4 text-center sm:px-6">
          <FaQuoteLeft className="mx-auto text-3xl text-emerald-500/40" />

          <blockquote className="mt-8 text-2xl font-bold leading-relaxed text-slate-200 sm:text-4xl">
            "The goal isn't to give your business more software.
            <br className="hidden sm:block" />
            It's to give you more clarity."
          </blockquote>

          <p className="mt-7 text-sm text-slate-500">
            MUYAZ BUSINESS OPERATING SYSTEM (MBOS)
          </p>
        </div>
      </section>
      {/* =====================================================
          PRICING
      ===================================================== */}
      {packagesError && (
        <div className="mb-8 rounded-xl border border-red-500/20 bg-red-950/30 p-4 text-center text-sm text-red-400">
          {packagesError}
        </div>
      )}

      <section id="pricing" className="py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-emerald-400">
              Simple pricing
            </p>

            <h2 className="mt-4 text-3xl font-black sm:text-5xl">
              Start small, Grow when you need to.
            </h2>

            <p className="mt-5 text-slate-400">
              Choose a plan that fits where your business is today.
            </p>
          </div>

          <div className="mt-16 grid gap-6 lg:grid-cols-4">
            {[...packages]
              .sort(
                (a, b) =>
                  ["STARTER", "BUSINESS", "GROWTH", "ENTERPRISE"].indexOf(
                    String(a.code || "").toUpperCase()
                  ) -
                  ["STARTER", "BUSINESS", "GROWTH", "ENTERPRISE"].indexOf(
                    String(b.code || "").toUpperCase()
                  )
              )
              .map((pkg) => {
                const code = String(pkg.code || "").toUpperCase();
                const pricingType = String(pkg.pricingType || "").toUpperCase();
                const isBusiness = code === "BUSINESS";
                const isGrowth = code === "GROWTH";
                const isEnterprise = pricingType === "CUSTOM";

                const features = getPackageFeatures(pkg);

                return (
                  <div
                    key={pkg.id || pkg.code}
                    className={`relative flex flex-col rounded-3xl border p-7 ${getPackageStyle(
                      pkg
                    )}`}
                  >
                    {/* BADGE */}
                    {isBusiness && (
                      <div className="absolute right-6 top-0 -translate-y-1/2 rounded-full bg-emerald-500 px-3 py-1 text-[9px] font-black tracking-wider text-slate-950">
                        MOST POPULAR
                      </div>
                    )}

                    {isGrowth && (
                      <div className="absolute right-6 top-0 -translate-y-1/2 rounded-full bg-sky-400 px-3 py-1 text-[9px] font-black tracking-wider text-slate-950">
                        ADVANCED
                      </div>
                    )}

                    {isEnterprise && (
                      <div className="absolute right-6 top-0 -translate-y-1/2 rounded-full bg-violet-400 px-3 py-1 text-[9px] font-black tracking-wider text-slate-950">
                        ENTERPRISE
                      </div>
                    )}

                    {/* PLAN */}
                    <h3 className="text-xl font-bold text-white">{code}</h3>

                    <p className="mt-3 min-h-[40px] text-sm text-slate-400">
                      {pkg.name}
                    </p>

                    {/* PRICE */}
                    <div className="mt-8">
                      {isEnterprise ? (
                        <>
                          <span className="text-4xl font-black text-white">
                            Custom
                          </span>

                          <span className="block mt-1 text-xs text-slate-500">
                            Tailored for your organization
                          </span>
                        </>
                      ) : (
                        <>
                          <span className="text-4xl font-black text-white">
                            {Number(pkg.price || 0).toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </span>

                          <span className="ml-2 text-sm text-slate-500">
                            ETB
                          </span>

                          <span className="block mt-1 text-xs text-slate-500">
                            / month
                          </span>
                        </>
                      )}
                    </div>

                    {/* USERS + AI */}
                    {!isEnterprise && (
                      <div className="grid grid-cols-2 gap-2 mt-6">
                        <div className="p-3 border rounded-xl bg-slate-950/50 border-white/5">
                          <FaUsers className="mb-2 text-emerald-400" />

                          <p className="text-[10px] font-bold uppercase text-slate-500">
                            Users
                          </p>

                          <p className="mt-1 text-sm font-bold text-white">
                            {pkg.includedUsers || 0}
                          </p>
                        </div>

                        <div className="p-3 border rounded-xl bg-slate-950/50 border-white/5">
                          <FaCodeBranch className="mb-2 text-amber-400" />
                          <p className="text-[10px] font-bold uppercase text-slate-500">
                            Unlimitted Branches
                          </p>
                        </div>
                      </div>
                    )}

                    {/* FEATURES */}
                    <ul className="flex-1 mt-8 space-y-4">
                      {features.map((feature) => (
                        <li
                          key={feature}
                          className="flex items-start gap-3 text-sm text-slate-300"
                        >
                          <FaCheckCircle className="mt-1 flex-shrink-0 text-emerald-400" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>

                    {/* BUTTON */}
                    <button
                      type="button"
                      onClick={() =>
                        isEnterprise ? setDemoOpen(true) : openAuth("login")
                      }
                      className={`flex items-center justify-center gap-2 w-full mt-10 rounded-xl py-3 text-sm font-bold transition ${getButtonStyle(
                        pkg
                      )}`}
                    >
                      {isEnterprise ? (
                        <>
                          <FaHeadset />
                          Talk to Us
                        </>
                      ) : (
                        <>
                          <FaRocket />
                          Start Free
                        </>
                      )}
                    </button>
                  </div>
                );
              })}
          </div>
        </div>
      </section>

      {/* =====================================================
          FINAL CTA
      ===================================================== */}
      <section className="px-4 pb-24 sm:px-6 lg:px-8">
        <div className="relative mx-auto max-w-7xl overflow-hidden rounded-[2rem] border border-emerald-500/20 bg-gradient-to-br from-emerald-600/20 via-slate-900 to-slate-950 px-6 py-20 text-center sm:px-12">
          <div className="absolute left-1/2 top-0 h-64 w-96 -translate-x-1/2 rounded-full bg-emerald-500/20 blur-[100px]" />

          <div className="relative">
            <h2 className="text-3xl font-black sm:text-5xl">
              Ready to know your business better?
            </h2>

            <p className="mx-auto mt-5 max-w-2xl text-slate-400">
              Start your journey with Muyaz and bring your sales, stock,
              expenses and accounting into one connected system.
            </p>

            <div className="mt-9 flex flex-col justify-center gap-3 sm:flex-row">
              <button
                type="button"
                onClick={() => setDemoOpen(true)}
                className="rounded-xl bg-emerald-600 px-7 py-3.5 text-sm font-bold shadow-xl shadow-emerald-600/25 transition hover:bg-emerald-500"
              >
                Book a Free Demo
              </button>

              <button
                type="button"
                onClick={() => openAuth("login")}
                className="rounded-xl border border-white/10 bg-white/5 px-7 py-3.5 text-sm font-bold transition hover:bg-white/10"
              >
                Start Free Trial
              </button>
            </div>
          </div>
        </div>
      </section>
      {/* =====================================================
          FOOTER
      ===================================================== */}
      <footer className="border-t border-white/5 py-10">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-6 px-4 text-center sm:flex-row sm:text-left sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center">
              <img
                src={logo}
                alt="Muyaz"
                className="h-full w-full object-contain"
              />
            </div>

            <div>
              <div className="font-bold">MBOS</div>

              <div className="text-[9px] uppercase tracking-widest text-slate-500">
                Muyaz Business Operating System
              </div>
            </div>
          </div>

          <p className="text-xs text-slate-500">
            © {new Date().getFullYear()} Muyaz Technologies PLC All rights
            reserved.
          </p>

          <div className="flex items-center gap-4 text-xs text-slate-500">
            <button
              onClick={() => openAuth("login")}
              className="hover:text-white"
            >
              Log in
            </button>

            <button
              onClick={() => setDemoOpen(true)}
              className="text-emerald-400 hover:text-emerald-300"
            >
              Contact us
            </button>
          </div>
        </div>
      </footer>
      {/* =====================================================
          DEMO MODAL
      ===================================================== */}
      <DemoModal
        open={demoOpen}
        onClose={() => setDemoOpen(false)}
        form={demoForm}
        onChange={handleDemoChange}
        onSubmit={handleDemoSubmit}
        onWhatsApp={handleWhatsAppSubmit}
        loading={demoLoading}
        error={demoError}
        success={demoSuccess}
      />
      {/* =====================================================
          AUTH MODAL
          ORIGINAL AUTH LOGIC PRESERVED
      ===================================================== */}
      {authOpen && (
        <div className="fixed inset-0 z-[1001] flex items-center justify-center overflow-y-auto bg-slate-950/70 p-4 backdrop-blur-md">
          <div className="relative w-full max-w-lg overflow-hidden rounded-3xl border border-white/20 bg-slate-900/95 shadow-2xl backdrop-blur-xl">
            <div className="h-1.5 bg-gradient-to-r from-emerald-500 via-teal-500 to-emerald-600" />

            <div className="relative max-h-[92vh] overflow-y-auto p-5 sm:p-8">
              <button
                type="button"
                className="absolute right-5 top-5 rounded-xl border border-white/5 bg-white/5 p-2 text-slate-400 transition hover:bg-white/10 hover:text-white"
                onClick={closeAuth}
                aria-label="Close"
              >
                <FaTimes className="text-xs" />
              </button>

              <div className="flex justify-center">
                <div className="flex h-10 w-10 items-center justify-center">
                  <img
                    src={logo}
                    alt="Muyaz"
                    className="h-full w-full object-contain"
                  />
                </div>
              </div>

              <div className="mt-4 text-center">
                <h2 className="text-2xl font-black">Welcome to MBOS</h2>

                <p className="mt-2 text-sm text-slate-400">
                  {view === "login" &&
                    "Sign in to continue managing your business."}

                  {view === "forgot" &&
                    "Enter your email to receive a password reset OTP."}

                  {view === "reset" &&
                    "Enter your OTP and choose a new password."}
                </p>
              </div>

              {authError && (
                <div className="mt-5 rounded-xl border border-red-500/20 bg-red-950/30 p-3">
                  <p className="text-center text-xs font-semibold text-red-400">
                    {authError}
                  </p>
                </div>
              )}

              {authSuccess && (
                <div className="mt-5 rounded-xl border border-emerald-500/20 bg-emerald-950/40 p-3">
                  <p className="text-center text-xs font-semibold text-emerald-400">
                    {authSuccess}
                  </p>
                </div>
              )}

              {/* LOGIN */}

              {view === "login" && (
                <form
                  onSubmit={handleCredentialLogin}
                  className="mt-7 space-y-4"
                >
                  <div>
                    <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
                      Email
                    </label>

                    <div className="relative mt-2">
                      <FaEnvelope className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />

                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="name@company.com"
                        required
                        className="w-full rounded-xl border border-white/10 bg-slate-950 py-3 pl-11 pr-4 text-sm outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between">
                      <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
                        Password
                      </label>

                      <button
                        type="button"
                        onClick={() => {
                          resetFeedback();
                          setView("forgot");
                        }}
                        className="text-xs text-emerald-400 hover:underline"
                      >
                        Forgot password?
                      </button>
                    </div>

                    <div className="relative mt-2">
                      <FaLock className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />

                      <input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="••••••••"
                        required
                        className="w-full rounded-xl border border-white/10 bg-slate-950 py-3 pl-11 pr-4 text-sm outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full rounded-xl bg-emerald-600 py-3 text-sm font-bold transition hover:bg-emerald-500 disabled:opacity-50"
                  >
                    {loading ? "Verifying…" : "Log in"}
                  </button>

                  <div className="flex items-center gap-3 py-2">
                    <div className="h-px flex-1 bg-white/10" />

                    <span className="text-[10px] uppercase tracking-widest text-slate-500">
                      Continue with Google
                    </span>

                    <div className="h-px flex-1 bg-white/10" />
                  </div>

                  <div className="flex justify-center">
                    <div className="w-full overflow-hidden rounded-xl bg-white p-0.5">
                      <GoogleLogin
                        theme="filled_blue"
                        size="large"
                        width="100%"
                        onSuccess={handleGoogleSuccess}
                        onError={() =>
                          setAuthError("Google Identity provider canceled.")
                        }
                      />
                    </div>
                  </div>
                </form>
              )}

              {/* FORGOT PASSWORD */}

              {view === "forgot" && (
                <form
                  onSubmit={handleForgotPassword}
                  className="mt-7 space-y-4"
                >
                  <div>
                    <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
                      Registered email
                    </label>

                    <div className="relative mt-2">
                      <FaEnvelope className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />

                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        className="w-full rounded-xl border border-white/10 bg-slate-950 py-3 pl-11 pr-4 text-sm outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full rounded-xl bg-emerald-600 py-3 text-sm font-bold disabled:opacity-50"
                  >
                    {loading ? "Sending…" : "Request reset"}
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      resetFeedback();
                      setView("login");
                    }}
                    className="w-full text-center text-xs text-slate-400 hover:text-white"
                  >
                    Return to login
                  </button>
                </form>
              )}

              {/* RESET PASSWORD */}

              {view === "reset" && (
                <form onSubmit={handleResetPassword} className="mt-7 space-y-4">
                  <div>
                    <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
                      Email
                    </label>

                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="mt-2 w-full rounded-xl border border-white/10 bg-slate-950 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>

                  <div>
                    <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
                      OTP
                    </label>

                    <div className="relative mt-2">
                      <FaKey className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />

                      <input
                        type="text"
                        value={token}
                        onChange={(e) => setToken(e.target.value)}
                        required
                        className="w-full rounded-xl border border-white/10 bg-slate-950 py-3 pl-11 pr-4 text-sm outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
                      New password
                    </label>

                    <div className="relative mt-2">
                      <FaLock className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />

                      <input
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        required
                        className="w-full rounded-xl border border-white/10 bg-slate-950 py-3 pl-11 pr-4 text-sm outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full rounded-xl bg-emerald-600 py-3 text-sm font-bold disabled:opacity-50"
                  >
                    {loading ? "Saving…" : "Submit"}
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      resetFeedback();
                      setView("login");
                    }}
                    className="w-full text-center text-xs text-slate-400 hover:text-white"
                  >
                    Cancel
                  </button>
                </form>
              )}

              <div className="mt-8 border-t border-white/5 pt-5 text-center text-[10px] text-slate-600">
                © {new Date().getFullYear()} Muyaz Technologies P.L.C
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Floating WhatsApp */}
      <a
        href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
          "Hello Muyaz team, I would like to book a demo of the Business OS."
        )}`}
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 flex items-center justify-center w-14 h-14 rounded-full bg-[#25D366] hover:bg-[#1ebe57] text-white shadow-lg shadow-emerald-900/40 transition hover:scale-105"
        aria-label="Chat on WhatsApp"
      >
        <svg viewBox="0 0 24 24" className="w-7 h-7 fill-current">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.435 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
        </svg>
      </a>
    </div>
  );
};

export default HomePage;
